import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useStore } from './hooks/useStore';
import Overview from './components/Overview';
import ManagerView from './components/ManagerView';
import BrandDetail from './components/BrandDetail';
import FollowersView from './components/FollowersView';
import SpecialDaysView from './components/SpecialDaysView';
import './App.css';

const MONTHS = [];
for (let y = 2026; y <= 2027; y++)
  for (let m = 1; m <= 12; m++)
    MONTHS.push(`${y}-${String(m).padStart(2,'0')}`);

function fmtMonthShort(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(y, mo-1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: .28, ease: [.4,0,.2,1] } },
  exit:    { opacity: 0, y: -8, transition: { duration: .18 } },
};

export default function App() {
  const store = useStore();
  const [view, setView]         = useState('overview');
  const [selMgr, setSelMgr]     = useState(null);
  const [selBrand, setSelBrand] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const navigate = (v, data = {}) => {
    setView(v);
    if (data.manager !== undefined) setSelMgr(data.manager);
    if (data.brand   !== undefined) setSelBrand(data.brand);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const { state } = store;
  const managers = state.managers;

  const pendingByMgr = (mgrId) => {
    const mBrands = state.brands.filter(b => b.managerId === mgrId);
    return mBrands.reduce((acc, b) => {
      const rec = store.getMonthRecord(b.id, state.currentMonth);
      return acc + (rec.tasks||[]).filter(t => t.status !== 'done' && t.status !== 'cancelled').length;
    }, 0);
  };

  const BottomNav = () => (
    <nav className="bottom-nav">
      <button className={`bn-item ${view==='overview'?'active':''}`} onClick={() => navigate('overview')}>
        <span className="bn-icon">⊞</span><span className="bn-label">Overview</span>
      </button>
      <button className={`bn-item ${view==='followers'?'active':''}`} onClick={() => navigate('followers')}>
        <span className="bn-icon">↑</span><span className="bn-label">Followers</span>
      </button>
      <button className={`bn-item ${view==='specialdays'?'active':''}`} onClick={() => navigate('specialdays')}>
        <span className="bn-icon">★</span><span className="bn-label">Special</span>
      </button>
    </nav>
  );

  const SideNav = () => (
    <aside className="side-nav">
      <button className={`sn-item ${view==='overview'?'active':''}`} onClick={() => navigate('overview')}>
        <span style={{fontSize:16}}>⊞</span> Overview
      </button>
      <div className="sn-section">Managers</div>
      {managers.map(m => {
        const p = pendingByMgr(m.id);
        const isActive = (view==='manager'&&selMgr===m.id)||(view==='brand'&&state.brands.find(b=>b.id===selBrand)?.managerId===m.id);
        return (
          <button key={m.id} className={`sn-item ${isActive?'active':''}`} onClick={() => navigate('manager', { manager: m.id })}>
            <span className="sn-dot" style={{ background: m.color }} />
            {m.name}
            {p > 0 && <span className="sn-badge">{p}</span>}
          </button>
        );
      })}
      <div className="sn-section">Tracking</div>
      <button className={`sn-item ${view==='followers'?'active':''}`} onClick={() => navigate('followers')}>
        <span style={{fontSize:16}}>↑</span> Followers
      </button>
      <button className={`sn-item ${view==='specialdays'?'active':''}`} onClick={() => navigate('specialdays')}>
        <span style={{fontSize:16}}>★</span> Special days
      </button>
    </aside>
  );

  return (
    <div className={`app layout-${isMobile?'mobile':'desktop'}`}>
      <header className="header">
        <span className="header-logo" onClick={() => navigate('overview')}>
          CEM<span> Ops</span>
        </span>
        {!isMobile && view === 'brand' && (() => {
          const brand = state.brands.find(b => b.id === selBrand);
          const mgr = brand ? managers.find(m => m.id === brand.managerId) : null;
          return (
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'var(--text2)' }}>
              <span style={{ cursor:'pointer', color:'var(--accent)' }} onClick={() => navigate('manager', { manager: brand?.managerId })}>{mgr?.name}</span>
              <span>›</span>
              <span style={{ color:'var(--text)' }}>{brand?.name}</span>
            </div>
          );
        })()}
        <select
          className="header-month"
          value={state.currentMonth}
          onChange={e => store.setCurrentMonth(e.target.value)}
        >
          {MONTHS.map(m => <option key={m} value={m}>{fmtMonthShort(m)}</option>)}
        </select>
      </header>

      {!isMobile && <SideNav />}

      {/* Loading overlay */}
      {state.loading && (
        <div style={{
          position:'fixed', inset:0, background:'var(--bg)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:999, flexDirection:'column', gap:12
        }}>
          <div style={{ fontFamily:'Syne,sans-serif', fontSize:30, fontWeight:800, color:'var(--text)' }}>
            CEM<span style={{color:'var(--accent)'}}> Ops</span>
          </div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>Connecting to database…</div>
        </div>
      )}

      <main className="main">
        <AnimatePresence mode="wait">
          <motion.div key={view+(selBrand||'')+(selMgr||'')} variants={pageVariants} initial="initial" animate="animate" exit="exit">
            {view === 'overview'    && <Overview    store={store} navigate={navigate} />}
            {view === 'manager'     && selMgr   && <ManagerView  store={store} managerId={selMgr}   navigate={navigate} />}
            {view === 'brand'       && selBrand && <BrandDetail  store={store} brandId={selBrand}   navigate={navigate} />}
            {view === 'followers'   && <FollowersView   store={store} />}
            {view === 'specialdays' && <SpecialDaysView store={store} navigate={navigate} />}
          </motion.div>
        </AnimatePresence>
      </main>

      {isMobile && <BottomNav />}
    </div>
  );
}
